import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowRight, Calculator, Fuel } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { CalculadoraSkeleton } from "@/components/skeletons";
import { BackToTop } from "@/components/BackToTop";
import { ArticleCTA } from "@/components/ArticleCTA";
import { seo, SITE_URL } from "@/lib/seo";

const PAGE_TITLE = "Calculadora Gasolina ou Etanol: a Regra dos 70%";
const PAGE_DESCRIPTION =
  "Informe o preço do litro do etanol e da gasolina e veja na hora qual compensa abastecer, com o cálculo da regra dos 70% e a economia por litro.";

export const Route = createFileRoute("/calculadoras/gasolina-etanol")({
  head: () => {
    const base = seo({
      path: "/calculadoras/gasolina-etanol",
      title: `${PAGE_TITLE} — Seu Bolso Tranquilo`,
      description: PAGE_DESCRIPTION,
      breadcrumbs: [
        { name: "Início", path: "/" },
        { name: "Calculadoras", path: "/calculadoras" },
        { name: "Gasolina x Etanol", path: "/calculadoras/gasolina-etanol" },
      ],
    });
    base.scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "Calculadora Gasolina x Etanol",
        url: `${SITE_URL}/calculadoras/gasolina-etanol`,
        applicationCategory: "FinanceApplication",
        operatingSystem: "Web",
        offers: { "@type": "Offer", price: "0", priceCurrency: "BRL" },
        description: PAGE_DESCRIPTION,
      }),
    });
    base.scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Como saber se vale a pena abastecer com álcool ou gasolina?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "A regra prática é dividir o preço do litro do etanol pelo preço do litro da gasolina. Se o resultado for menor ou igual a 0,70 (70%), o etanol compensa. Se for maior que 0,70, a gasolina é mais vantajosa.",
            },
          },
          {
            "@type": "Question",
            name: "Por que o corte é 70%?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "O etanol tem menor poder calorífico que a gasolina, ou seja, o motor consome mais litros de etanol para rodar a mesma distância. A relação de 70% é a regra empírica usada no Brasil para compensar essa diferença de autonomia.",
            },
          },
        ],
      }),
    });
    return base;
  },
  component: FuelCalculatorPage,
  pendingComponent: CalculadoraSkeleton,
});

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 3 });

function FuelCalculatorPage() {
  const [ethanolPrice, setEthanolPrice] = useState<number | "">("");
  const [gasolinePrice, setGasolinePrice] = useState<number | "">("");

  const result = useMemo(() => {
    const ethanol = Number(ethanolPrice);
    const gasoline = Number(gasolinePrice);
    if (!ethanol || !gasoline || gasoline <= 0) return null;
    const ratio = ethanol / gasoline;
    const ethanolWins = ratio <= 0.7;
    return { ratio, ethanolWins };
  }, [ethanolPrice, gasolinePrice]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo className="size-8" alt="Logo Seu Bolso Tranquilo" />
            <span className="font-semibold">Seu Bolso Tranquilo</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Início</Link>
            <Link to="/aprenda" className="hover:text-foreground transition-colors">Aprenda</Link>
            <Link to="/contato" className="hover:text-foreground transition-colors">Contato</Link>
          </nav>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Começar grátis
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-foreground">Início</Link>
          <span className="mx-2">/</span>
          <Link to="/calculadoras" className="hover:text-foreground">Calculadoras</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Gasolina x Etanol</span>
        </nav>

        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-4">
            <Calculator className="size-4" />
            Calculadora gratuita
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            Gasolina x Etanol
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Informe o preço do litro do etanol e da gasolina e descubra qual combustível vale mais a
            pena para o seu bolso — com a regra prática dos 70%.
          </p>
        </div>

        <section className="grid lg:grid-cols-[1fr_1.2fr] gap-6 mb-12">
          <form
            className="rounded-3xl border border-border/60 bg-card p-6 sm:p-8 space-y-5"
            onSubmit={(e) => e.preventDefault()}
            aria-label="Preços dos combustíveis"
          >
            <h2 className="text-lg font-semibold mb-2">Preços</h2>

            <label className="block">
              <span className="text-sm font-medium mb-1.5 block">Preço do litro do etanol (R$)</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={ethanolPrice}
                placeholder="Ex: 3,89"
                onChange={(e) => {
                  const value = e.target.value;
                  setEthanolPrice(value === "" ? "" : Math.max(0, Number(value) || 0));
                }}
                className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-base"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium mb-1.5 block">Preço do litro da gasolina (R$)</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={gasolinePrice}
                placeholder="Ex: 5,49"
                onChange={(e) => {
                  const value = e.target.value;
                  setGasolinePrice(value === "" ? "" : Math.max(0, Number(value) || 0));
                }}
                className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-base"
              />
            </label>
          </form>

          <div
            className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-background to-primary-glow/5 p-6 sm:p-8"
            aria-live="polite"
          >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-3">
              <Fuel className="size-4" />
              Resultado
            </div>

            {result ? (
              <>
                <div className="mb-6">
                  <div className="text-sm text-muted-foreground">Relação etanol / gasolina</div>
                  <div className="text-4xl sm:text-5xl font-bold tracking-tight mt-1">
                    {(result.ratio * 100).toFixed(1).replace(".", ",")}%
                  </div>
                </div>
                <div className="rounded-2xl border border-border/60 bg-background/60 p-4 mb-6">
                  <div className="text-sm text-muted-foreground mb-1">Melhor opção</div>
                  <div className={`text-2xl font-semibold ${result.ethanolWins ? "text-emerald-500" : "text-amber-500"}`}>
                    {result.ethanolWins ? "Álcool (etanol) vale a pena" : "Gasolina é a melhor opção"}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {result.ethanolWins
                    ? "O etanol custa até 70% do preço da gasolina, então compensa abastecer com álcool."
                    : "O etanol custa mais de 70% do preço da gasolina, então a gasolina tem melhor custo-benefício."}
                </p>
              </>
            ) : (
              <div className="h-full flex flex-col justify-center text-muted-foreground">
                <p className="text-lg">Preencha os dois preços para ver a recomendação.</p>
              </div>
            )}
          </div>
        </section>

        <ArticleCTA
          variant="final"
          headline="Economize também nos seus investimentos"
          body="Crie sua conta grátis no Seu Bolso Tranquilo e acompanhe seus investimentos em renda fixa, ações e FIIs, com projeções automáticas, benchmarks e análise por IA."
        />

        <section className="prose prose-invert max-w-3xl mx-auto mt-12">
          <h2>Como funciona a regra dos 70%?</h2>
          <p>
            A regra prática para decidir entre gasolina e etanol é dividir o preço do litro do
            álcool pelo preço da gasolina. Se o resultado for <strong>igual ou inferior a 0,70</strong>{" "}
            (ou 70%), o etanol é mais vantajoso. Se for <strong>maior que 0,70</strong>, a gasolina é
            a melhor escolha.
          </p>
          <h2>Por que exatamente 70%?</h2>
          <p>
            O etanol tem menor poder calorífico que a gasolina. Em média, um carro flex consome
            cerca de 30% mais etanol para rodar a mesma distância. Por isso, o etanol só compensa
            financeiramente quando custa até 70% do preço da gasolina.
          </p>
          <h2>Exemplo prático</h2>
          <p>
            Se a gasolina custa R$ 5,00 e o etanol custa R$ 3,40, a relação é 3,40 ÷ 5,00 = 0,68
            (68%). Como é menor que 70%, o etanol vale a pena. Se o etanol custasse R$ 3,70, a
            relação seria 0,74 (74%) — aí a gasolina seria mais econômica.
          </p>
        </section>
      </main>

      <BackToTop />
    </div>
  );
}
